import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { edgeFunctionError } from "@/lib/edgeFunctionError"
import { supabase } from "@/lib/supabase"

export type AccessRole = "admin" | "operator" | "partner"

/** The two roles a HIVED Google account can hold. A partner is added another way: see below. */
export type StaffRole = Exclude<AccessRole, "partner">

export interface AccessEntry {
  email: string
  role: AccessRole
  /** The one operator a partner login sees. Null for admin and operator. */
  operator_scope: string | null
  /**
   * When a partner signed up with this address, or null while nobody has. Always null for admin
   * and operator, whose accounts are Google's.
   */
  signed_up_at: string | null
  note: string | null
  created_at: string
  created_by: string | null
}

/** A company that runs routes, and how its drivers are told apart in the Drivers CRM. */
export interface OperatorEntry {
  name: string
  driver_crm_base: string | null
}

const KEY = ["collections-access"]
const OPERATORS_KEY = ["collections-operators"]

/**
 * The operator every route carries unless somebody says otherwise. Never offered as a partner's
 * scope: a login scoped to it would be shown nearly every route there is.
 */
export const HOUSE_OPERATOR = "HIVED"

/**
 * The access list is the one table the frontend reads and writes directly
 * rather than through the collections edge function. Every other table is
 * reached with a service-role key, where authorisation is the function's job;
 * here authorisation depends on the caller's own JWT, so it belongs in an RLS
 * policy that the database enforces. A non-admin gets an empty list and a
 * refused write regardless of what the UI allows.
 */
export function useAccessList() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<AccessEntry[]> => {
      const { data, error } = await supabase
        .from("collections_access")
        .select("email, role, operator_scope, signed_up_at, note, created_at, created_by")
        .order("role", { ascending: true })
        .order("email", { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []) as AccessEntry[]
    },
  })
}

export function useGrantAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entry: {
      email: string
      role: StaffRole
      note?: string
      createdBy: string
    }) => {
      // The table has a lowercase check constraint; normalise here so a typo in
      // capitalisation is a no-op rather than a constraint violation.
      const email = entry.email.trim().toLowerCase()
      if (!email) throw new Error("Email is required")
      const { error } = await supabase.from("collections_access").insert({
        email,
        role: entry.role,
        note: entry.note?.trim() || null,
        created_by: entry.createdBy,
      })
      if (error) {
        throw new Error(
          error.code === "23505" ? `${email} is already on the list` : error.message
        )
      }
      return email
    },
    onSuccess: (email) => {
      toast.success(`${email} can now access collections`)
      void qc.invalidateQueries({ queryKey: KEY })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useSetAccessRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ email, role }: { email: string; role: StaffRole }) => {
      const { error } = await supabase
        .from("collections_access")
        .update({ role })
        .eq("email", email)
      if (error) throw new Error(error.message)
      return { email, role }
    },
    onSuccess: ({ email, role }) => {
      toast.success(`${email} is now ${role}`)
      void qc.invalidateQueries({ queryKey: KEY })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useRevokeAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.from("collections_access").delete().eq("email", email)
      if (error) throw new Error(error.message)
      return email
    },
    onSuccess: (email) => {
      // The API reads this list on every request, so the app stops answering them now. Only the
      // role claim on their token outlives the row, until its next refresh.
      toast.success(`${email} removed`)
      void qc.invalidateQueries({ queryKey: KEY })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

// ---------------------------------------------------------------------------
// Partner logins
//
// A partner company's dispatcher has no HIVED Google account. An admin adds their address here
// with the company it is for, and they then sign up themselves, with a password nobody at HIVED
// sets or sees. Adding is a row like any other; what an admin cannot do from the browser is
// touch the account a sign-up made, which is what collections-admin-access is for.
// ---------------------------------------------------------------------------

/** Call collections-admin-access, and surface the function's own message when it refuses. */
async function adminAccess(body: Record<string, unknown>): Promise<void> {
  const { data, error } = await supabase.functions.invoke("collections-admin-access", { body })
  if (error) throw new Error((await edgeFunctionError(error)) ?? error.message)
  if ((data as { success?: boolean } | null)?.success === false) {
    throw new Error((data as { error?: string }).error ?? "Something went wrong")
  }
}

export function useAddPartner() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entry: {
      email: string
      operator: string
      note?: string
      createdBy: string
    }) => {
      const email = entry.email.trim().toLowerCase()
      if (!email) throw new Error("Email is required")
      const { error } = await supabase.from("collections_access").insert({
        email,
        role: "partner",
        operator_scope: entry.operator,
        note: entry.note?.trim() || null,
        created_by: entry.createdBy,
      })
      if (error) {
        throw new Error(
          error.code === "23505" ? `${email} is already on the list` : error.message
        )
      }
      return email
    },
    onSuccess: (email) => {
      toast.success(`${email} can now sign up`, {
        description: "Tell them to use Sign up on the sign-in page, with this email address.",
      })
      void qc.invalidateQueries({ queryKey: KEY })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

/**
 * Forget a partner's sign-up, so the address can sign up again.
 *
 * The answer to a forgotten password, since there is no mail server to send a reset link
 * through -- and to a sign-up that turns out not to have been theirs.
 */
export function useResetPartner() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (email: string) => {
      await adminAccess({ action: "reset_partner", email })
      return email
    },
    onSuccess: (email) => {
      toast.success(`${email} can sign up again`)
      void qc.invalidateQueries({ queryKey: KEY })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useRemovePartner() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (email: string) => {
      await adminAccess({ action: "remove_partner", email })
      return email
    },
    onSuccess: (email) => {
      // Unlike a HIVED account, this one ends now: the API reads the list on every request, and
      // the login itself is deleted.
      toast.success(`${email} removed`)
      void qc.invalidateQueries({ queryKey: KEY })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

// ---------------------------------------------------------------------------
// Operators
// ---------------------------------------------------------------------------

export function useOperatorList() {
  return useQuery({
    queryKey: OPERATORS_KEY,
    queryFn: async (): Promise<OperatorEntry[]> => {
      const { data, error } = await supabase
        .from("collections_operators")
        .select("name, driver_crm_base")
        .order("name", { ascending: true })
      if (error) throw new Error(error.message)
      return data ?? []
    },
  })
}

export function useAddOperator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entry: { name: string; driverCrmBase?: string; createdBy: string }) => {
      const name = entry.name.trim()
      if (!name) throw new Error("Company name is required")
      const { error } = await supabase.from("collections_operators").insert({
        name,
        driver_crm_base: entry.driverCrmBase?.trim() || null,
        created_by: entry.createdBy,
      })
      if (error) {
        throw new Error(error.code === "23505" ? `${name} is already listed` : error.message)
      }
      return name
    },
    onSuccess: (name) => {
      toast.success(`${name} added`)
      void qc.invalidateQueries({ queryKey: OPERATORS_KEY })
      // The planning dialog's operator options come from the same table, by another road.
      void qc.invalidateQueries({ queryKey: ["collections", "operators"] })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useSetOperatorCrmBase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ name, driverCrmBase }: { name: string; driverCrmBase: string }) => {
      const { error } = await supabase
        .from("collections_operators")
        .update({ driver_crm_base: driverCrmBase.trim() || null })
        .eq("name", name)
      if (error) throw new Error(error.message)
      return name
    },
    onSuccess: (name) => {
      toast.success(`${name}'s drivers updated`)
      void qc.invalidateQueries({ queryKey: OPERATORS_KEY })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}
